import { useTranslation } from 'react-i18next'
import { Select } from '@/components/ui'
import { useGlAccounts } from '@/hooks/useAccounting'
import { useGlAccountLabel } from '@/hooks/useGlAccountLabel'
import type { ExpenseCategory } from '@/types'

interface ExpenseCategoryGlSelectProps {
  category: ExpenseCategory
  onChange: (code: string) => void
}

export function ExpenseCategoryGlSelect(props: ExpenseCategoryGlSelectProps) {
  const { t } = useTranslation('settings')
  if (props.category.key === 'deposit_return') {
    return <p className="text-sm text-base-content/60">{t('expense_categories.deposit_return_gl')}</p>
  }
  return <EditableGlSelect {...props} />
}

function EditableGlSelect({ category, onChange }: ExpenseCategoryGlSelectProps) {
  const { t, i18n } = useTranslation('settings')
  const { data, isLoading, isError } = useGlAccounts({ active: true })
  const accountLabel = useGlAccountLabel()
  const name = i18n.language === 'th' ? category.th : category.en
  const options = (data?.data ?? [])
    .filter((account) => account.active && account.account_type === 'expense')
    .map((account) => ({
      value: account.code,
      label: `${account.code} — ${accountLabel(account.code, account.system, i18n.language === 'th' && account.name_th ? account.name_th : account.name)}`,
    }))
  const hasSelection = options.some((option) => option.value === category.gl_code)

  return (
    <Select
      id={`expense-category-gl-${category.key}`}
      aria-label={t('expense_categories.gl_account_for', { name })}
      size="sm"
      value={hasSelection ? category.gl_code : ''}
      onChange={(event) => onChange(event.target.value)}
      options={options}
      placeholder={isLoading
        ? t('expense_categories.loading_gl_accounts')
        : category.gl_code && !hasSelection
          ? t('expense_categories.unavailable_gl_account', { code: category.gl_code })
          : t('expense_categories.select_gl_account')}
      disabled={isLoading || isError || options.length === 0}
      error={isError ? t('expense_categories.gl_accounts_error') : undefined}
      hint={!isLoading && !isError && options.length === 0 ? t('expense_categories.no_gl_accounts') : undefined}
      className="w-full min-w-0 sm:w-64"
    />
  )
}
